import React, { useState, useEffect, useRef } from "react";
import { FaCheck, FaUpload, FaSpinner, FaFileAlt, FaLink, FaPaperPlane } from "react-icons/fa";
import { toast } from "react-toastify";
import { httpService } from "../../services/httpService";
import { uploadFileToS3 } from "./uploadService";
import { useProfile } from "../../context/ProfileContext";
import { buildFormPublicLink } from "../../utils/formLinkHelper";
import config from "../../config";

const OrderFormsSection = ({
  orderId,
  item,
  clientPhone,
  onMandatoryStatusChange,
  onFormIdsChange,
  isBypassed = false,
  onBypassChange = () => {},
  hideBypassBlock = false,
  hideTitle = false,
  itemKey: propItemKey,
}) => {
  const { profileData } = useProfile();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingFormId, setUploadingFormId] = useState(null);
  const [sendingFormId, setSendingFormId] = useState(null);
  const fileInputRef = useRef(null);
  const [activeFormForUpload, setActiveFormForUpload] = useState(null);
  const uploadedFormIdsRef = useRef(new Set());

  // ── Fetch order forms status ──────────────────────────────────────────────
const fetchOrderForms = async () => {
  if (!orderId || orderId === "undefined") return;

  setLoading(true);

  // Extract item's form_ids if passed on the order item
  const rawItemFormIds = item?.form_ids || (item?.form_id ? [item.form_id] : []);
  const itemFormIds = Array.isArray(rawItemFormIds)
    ? rawItemFormIds
        .map((f) => (typeof f === "object" ? String(f._id || f.form_id || f.id || "") : String(f)))
        .filter(Boolean)
    : [];

  const itemRefId = String(item?.ref_id || item?.id || item?._id || "");
  const itemId = String(item?._id || item?.item_id || "");
  const petId = String(item?.vendor_pet_id || item?.pet_id || "");
  const itemType = String(item?.item_type || item?.type || "").toLowerCase();

  try {
    const res = await httpService.get(`/vendors/getOrderFormsStatus/${String(orderId)}`, {
      skipToast: true,
    });

    // 1. Robust response parsing
    const rawData = res?.data?.data || res?.data || res || {};
    const allForms = Array.isArray(rawData?.forms)
      ? rawData.forms
      : Array.isArray(rawData)
      ? rawData
      : Array.isArray(res?.forms)
      ? res.forms
      : [];

    // 2. Filter relevant forms for this item
    let relevantForms = allForms.filter((f) => {
      const fFormId = String(f.form_id || f._id || "");
      const formItemRefId = String(f.item_ref_id || "");
      const formItemId = String(f.item_id || "");
      const formPetId = String(f.pet_id || "");
      const formItemType = String(f.item_type || "").toLowerCase();

      // Explicit Form ID Match (if item has form_ids attached)
      if (fFormId && itemFormIds.length > 0 && itemFormIds.includes(fFormId)) {
        return true;
      }

      // Direct ID Match
      if (itemRefId && (formItemRefId === itemRefId || formItemId === itemRefId)) {
        return true;
      }
      if (itemId && (formItemId === itemId || formItemRefId === itemId)) {
        return true;
      }

      // Pet & Service Type Match
      if (petId && formPetId && formPetId === petId) {
        if (!formItemType || !itemType || formItemType === itemType) {
          return true;
        }
      }

      // Service Type Fallback Match (e.g. both are "boarding")
      if (itemType && formItemType && itemType === formItemType) {
        return true;
      }

      // Unassigned / Global Order Forms
      if (!formItemRefId && !formItemId && !formPetId && itemFormIds.length === 0) {
        return true;
      }

      return false;
    });

    // Fallback: If forms exist for the order but filter produced no match, display allForms or match itemFormIds via getAllForms
    if (relevantForms.length === 0 && itemFormIds.length > 0) {
      try {
        const allFormsRes = await httpService.get("/vendors/getAllForms", { skipToast: true });
        const globalForms = allFormsRes?.data || allFormsRes || [];
        if (Array.isArray(globalForms)) {
          const matchedGlobal = globalForms.filter((gf) =>
            itemFormIds.includes(String(gf._id || gf.form_id || gf.id || ""))
          );
          if (matchedGlobal.length > 0) {
            relevantForms = matchedGlobal.map((gf) => ({
              form_id: String(gf._id || gf.form_id || gf.id || ""),
              form_name: gf.form_name || gf.name || gf.label || "Order Form",
              requirement: gf.requirement || "optional",
              form_frequency: gf.form_frequency || "every_time",
              completed: false,
              status: "pending_send",
              item_ref_id: itemRefId,
              pet_id: petId,
            }));
          }
        }
      } catch (e) {
        console.warn("Fallback getAllForms failed:", e);
      }
    } else if (relevantForms.length === 0 && allForms.length > 0) {
      relevantForms = allForms;
    }

    // Merge locally uploaded form IDs or answer IDs
    const finalForms = relevantForms.map((f) => {
      const fId = String(f.form_id || f._id || "");
      const answerId = String(f.answer_id || f.id || "");
      if (
        uploadedFormIdsRef.current.has(fId) ||
        (answerId && uploadedFormIdsRef.current.has(answerId))
      ) {
        return { ...f, completed: true, status: "completed" };
      }
      return f;
    });

    setForms(finalForms);

    const formIds = finalForms
      .map((f) => f.form_id || f._id)
      .filter(Boolean);

    if (onFormIdsChange) {
      onFormIdsChange(itemRefId || itemId, formIds);
    }

    // Update mandatory form status
    const hasPendingMandatory = finalForms.some(
      (f) =>
        f.requirement === "mandatory" &&
        !(f.completed || f.status === "completed")
    );

    if (onMandatoryStatusChange) {
      onMandatoryStatusChange(itemRefId || itemId, hasPendingMandatory);
    }
  } catch (err) {
    console.error("Error fetching order forms status:", err);
    let fallbackForms = [];
    if (itemFormIds.length > 0) {
      try {
        const allFormsRes = await httpService.get("/vendors/getAllForms", { skipToast: true });
        const globalForms = allFormsRes?.data || allFormsRes || [];
        if (Array.isArray(globalForms)) {
          const matchedGlobal = globalForms.filter((gf) =>
            itemFormIds.includes(String(gf._id || gf.form_id || gf.id || ""))
          );
          fallbackForms = matchedGlobal.map((gf) => ({
            form_id: String(gf._id || gf.form_id || gf.id || ""),
            form_name: gf.form_name || gf.name || gf.label || "Order Form",
            requirement: gf.requirement || "optional",
            form_frequency: gf.form_frequency || "every_time",
            completed: false,
            status: "pending_send",
            item_ref_id: itemRefId,
            pet_id: petId,
          }));
        }
      } catch (e) {}
    }
    setForms(fallbackForms);

    const hasPendingMandatory = fallbackForms.some(
      (f) =>
        f.requirement === "mandatory" &&
        !(f.completed || f.status === "completed")
    );

    if (onMandatoryStatusChange) {
      onMandatoryStatusChange(itemRefId || itemId, hasPendingMandatory);
    }
    if (onFormIdsChange) {
      onFormIdsChange(itemRefId || itemId, itemFormIds);
    }
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchOrderForms();
  }, [orderId, item?.ref_id, item?._id]);

  // ── Trigger File Upload ───────────────────────────────────────────────────
  const handleUploadClick = (form) => {
    setActiveFormForUpload(form);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeFormForUpload) return;

    const formId = activeFormForUpload.form_id || activeFormForUpload._id;
    setUploadingFormId(formId);

    try {
      // 1. Upload file directly to AWS S3 using project's standard S3 upload helper
      let fileUrl = "";
      try {
        fileUrl = await uploadFileToS3(file, {
          returnType: "fullUrl",
          module: "vendors",
          docType: "forms",
        });
      } catch (s3Err) {
        // Fallback: direct httpService POST to /upload/fileUpload with FormData
        const formData = new FormData();
        formData.append("module", "vendors");
        formData.append("docType", "forms");
        formData.append("file", file);

        const uploadRes = await httpService.post("/upload/fileUpload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const s3Key = uploadRes?.data?.s3Key;
        fileUrl =
          uploadRes?.data?.fullUrl ||
          uploadRes?.data?.location ||
          uploadRes?.data?.url ||
          (s3Key ? `${config.S3_BASE_URL || process.env.REACT_APP_S3_BASE_URL || ""}/${s3Key}` : "");
      }

      if (!fileUrl) {
        throw new Error("Failed to obtain uploaded file S3 URL.");
      }

      // 2. Submit form response API with the S3 file URL
      const formId = activeFormForUpload.form_id || activeFormForUpload._id;
      const answerId = activeFormForUpload.answer_id || activeFormForUpload.answerId || activeFormForUpload._id || "";
      const itemRefId = activeFormForUpload.item_ref_id || activeFormForUpload.itemRefId || item?.ref_id || item?.id || null;
      const itemId = activeFormForUpload.item_id || activeFormForUpload.itemId || item?._id || item?.item_id || "";
      const petId = activeFormForUpload.pet_id || item?.vendor_pet_id || item?.pet_id || "";
      const resolvedVendorId =
        activeFormForUpload.vendor_id ||
        activeFormForUpload.vendorId ||
        item?.vendor_id ||
        profileData?._id ||
        "";
      const resolvedClientId =
        activeFormForUpload.vendor_client_id ||
        activeFormForUpload.client_id ||
        item?.vendor_client_id ||
        item?.client_id ||
        "";
      const resolvedPhone =
        clientPhone ||
        item?.client_phone ||
        item?.phone ||
        activeFormForUpload?.phone ||
        "";

      const qId =
        activeFormForUpload.question_id ||
        activeFormForUpload.questions?.[0]?._id ||
        activeFormForUpload.questions?.[0]?.id ||
        formId;

      const qAnswers = [
        {
          question_id: qId,
          question: activeFormForUpload.form_name || "Uploaded Form Document",
          answer: fileUrl,
          file_url: fileUrl,
        },
      ];

      const submitPayload = {
        answer_id: answerId,
        answerId: answerId,
        form_response_id: answerId,
        phoneNumber: resolvedPhone,
        phone_number: resolvedPhone,
        phone: resolvedPhone,
        petId: petId,
        pet_id: petId,
        orderId: orderId,
        order_id: orderId,
        vendorId: resolvedVendorId,
        vendor_id: resolvedVendorId,
        vendorClientId: resolvedClientId,
        vendor_client_id: resolvedClientId,
        appointmentOrBoardingId: itemRefId,
        appointment_or_boarding_id: itemRefId,
        itemRefId: itemRefId,
        item_ref_id: itemRefId,
        itemId: itemId,
        item_id: itemId,
        formId: formId,
        form_id: formId,
        url: fileUrl,
        document_url: fileUrl,
        file_url: fileUrl,
        status: "completed",
        completed: true,
        questionAnswers: qAnswers,
        question_answers: qAnswers,
      };

      const endpoint = resolvedVendorId && formId
        ? `/consentForm/submitFormResponse/${resolvedVendorId}/${formId}`
        : "/consentForm/submitConsentAnswer";

      await httpService.post(endpoint, submitPayload);

      // Track session-uploaded form ID and answer ID
      const uploadedFormIdStr = String(formId);
      uploadedFormIdsRef.current.add(uploadedFormIdStr);
      if (answerId) {
        uploadedFormIdsRef.current.add(String(answerId));
      }

      setForms((prevForms) => {
        const updated = prevForms.map((f) => {
          const fId = String(f.form_id || f._id || "");
          const fAnsId = String(f.answer_id || f.id || "");
          if (
            fId === uploadedFormIdStr ||
            (answerId && fAnsId === String(answerId)) ||
            (f.form_name && f.form_name === activeFormForUpload.form_name)
          ) {
            return { ...f, completed: true, status: "completed" };
          }
          return f;
        });

        const hasPending = updated.some(
          (f) =>
            f.requirement === "mandatory" &&
            !(f.completed || f.status === "completed")
        );

        if (onMandatoryStatusChange) {
          onMandatoryStatusChange(itemRefId || itemId, hasPending);
        }

        return updated;
      });

      toast.success(`Form "${activeFormForUpload.form_name}" uploaded to S3 successfully!`);
      await fetchOrderForms();
    } catch (err) {
      console.error("Form S3 upload error:", err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to upload form document.");
    } finally {
      setUploadingFormId(null);
      setActiveFormForUpload(null);
    }
  };

  const buildFormLink = (form) => {
    const bottomFormId = String(form.form_id || form._id || form.id || "");
    const vendorId = profileData?._id || profileData?.id || "";
    if (!bottomFormId) return null;
    const query = orderId ? `orderId=${encodeURIComponent(orderId)}` : "";
    return buildFormPublicLink(vendorId, bottomFormId, form.formLink, query);
  };

  const handleCopyLink = (form) => {
    const link = buildFormLink(form);
    if (!link) {
      toast.error("Unable to build form link.");
      return;
    }
    navigator.clipboard.writeText(link);
    toast.success("Form link copied to clipboard!");
  };

  const normalizeServiceTypeName = () => {
    const rawType = String(item?.service_type_name || item?.item_type || item?.service_name || "").toLowerCase();
    if (rawType.includes("boarding")) return "boarding";
    if (rawType.includes("grooming")) return "grooming";
    if (rawType.includes("pet vet") || rawType.includes("vet") || rawType.includes("clinic")) return "pet_vet";
    if (rawType.includes("day care") || rawType.includes("daycare")) return "boarding";
    if (rawType.includes("dog walking")) return "dog_walking";
    return rawType || "boarding";
  };

  const handleSendForm = async (form) => {
    const formId = String(form.form_id || form._id || form.id || "");
    const serviceRefId = item?.ref_id || item?.booking_id || item?.id || item?._id;
    if (!serviceRefId || !formId) {
      toast.error("Cannot send form: missing booking or form reference.");
      return;
    }

    setSendingFormId(formId);
    try {
      await httpService.post("/vendororder/sendConsentForm", {
        serviceRefId,
        serviceTypeName: normalizeServiceTypeName(),
        formName: form.form_name || form.name || form.title || "",
      });
      toast.success("Consent form send request submitted.");
    } catch (err) {
      console.error("Failed to send consent form:", err);
      toast.error(err?.response?.data?.message || "Failed to send form.");
    } finally {
      setSendingFormId(null);
    }
  };

  const itemKey =
    propItemKey ||
    String(item?.ref_id || item?.id || item?._id || item?.item_id || "");
  const canBypass = Array.isArray(forms) && forms.length > 0 && forms.some((f) => !(f.completed || f.status === "completed"));

  return (
    <div style={{ marginTop: 0, borderTop: "none", paddingTop: 0 }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: "none" }}
        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
      />

      {!hideTitle && (
        <div
          style={{
            fontSize: 12,
            color: "#111827",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>Required Forms</span>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {loading && (
              <FaSpinner className="animate-spin text-gray-400" size={12} />
            )}
            {!hideBypassBlock && canBypass && (
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  opacity: 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={isBypassed}
                  onChange={(e) => {
                    
                    onBypassChange(itemKey, e.target.checked);
                  }}
                  style={{ width: 14, height: 14, cursor: "pointer" }}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: isBypassed ? "#065f46" : "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  Bypass forms for check-in
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ fontSize: 13, color: "#9ca3af", padding: "4px 0" }}>
          Loading forms…
        </div>
      ) : forms.length === 0 ? (
        <div style={{ fontSize: 12, color: "#9ca3af", padding: "4px 0" }}>
          No forms required for this service.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {forms.map((form) => {
            const isCompleted = Boolean(
              form.completed || form.status === "completed",
            );
            const isMandatory = form.requirement === "mandatory";
            const isUploading = uploadingFormId === (form.form_id || form._id);

            return (
              <div
                key={form.form_id || form._id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 10px",
                  borderRadius: 8,
                  background: isCompleted
                    ? "#f0fdf4"
                    : isMandatory
                      ? "#fef2f2"
                      : "#f9fafb",
                  border: `1px solid ${
                    isCompleted
                      ? "#86efac"
                      : isMandatory
                        ? "#fca5a5"
                        : "#e5e7eb"
                  }`,
                  transition: "all 0.2s ease",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <FaFileAlt
                    size={14}
                    color={
                      isCompleted
                        ? "#16a34a"
                        : isMandatory
                          ? "#dc2626"
                          : "#6b7280"
                    }
                  />
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: isCompleted
                          ? "#065f46"
                          : isMandatory
                            ? "#991b1b"
                            : "#374151",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {form.form_name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "#6b7280",
                        display: "flex",
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: isMandatory ? "#b91c1c" : "#6b7280",
                        }}
                      >
                        {isMandatory ? "Mandatory" : "Optional"}
                      </span>
                      {form.form_frequency && (
                        <span>• {form.form_frequency.replace("_", " ")}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexShrink: 0,
                  }}
                >
                  {isCompleted ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 8px",
                        borderRadius: 6,
                        background: "#16a34a",
                        color: "#fff",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      <FaCheck size={10} /> Completed
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleUploadClick(form)}
                        disabled={isUploading}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "3px 8px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#374151",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: isUploading ? "not-allowed" : "pointer",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                      >
                        {isUploading ? (
                          <>
                            <FaSpinner
                              className="animate-spin text-blue-600"
                              size={12}
                            />
                            Uploading…
                          </>
                        ) : (
                          <>
                            <FaUpload size={11} color="#2563eb" />
                            Upload Form
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleCopyLink(form)}
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#374151",
                          cursor: "pointer",
                        }}
                      >
                        <FaLink size={14} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSendForm(form)}
                        disabled={sendingFormId === (form.form_id || form._id)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "3px 8px",
                          borderRadius: 6,
                          border: "1px solid #d1d5db",
                          background: "#fff",
                          color: "#374151",
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                        }}
                      >
                        {sendingFormId === (form.form_id || form._id) ? (
                          <>
                            <FaSpinner
                              className="animate-spin text-blue-600"
                              size={12}
                            />
                            Sending…
                          </>
                        ) : (
                          <>
                            <FaPaperPlane size={11} color="#2563eb" />
                            Send
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderFormsSection;